// Partner-Report — the numbers we would actually hand ONE partner: reach,
// cost, quality and a content profile, scoped to that partner's embed origins.
//
// Two things separate this from the other panels. It owns its tenant picker
// (a tenant is mandatory here, not a filter), and the content breakdowns are
// k-suppressed server-side — anything leaving the building is an external
// report, so min_n stays at 10 unless someone deliberately lowers it for an
// internal look. The caveats are rendered, not buried: an under-reported
// figure that nobody can explain is worse than a missing one.

import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { PartnerReportResponse } from "@/api/types";
import { FeedbackCounts } from "@/components/FeedbackCounts";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import {
  formatDecimal,
  formatInt,
  formatPercent,
  formatUsd,
  modeLabel,
  tenantLabel,
} from "@/lib/format";
import { includeTestSessions, partnerWindow } from "@/state/filters";

// 10 is the k≥10 doctrine used by the weekly aggregates and Talent-Geografie.
// The lower values exist for an internal look only.
const MIN_N_CHOICES = [1, 5, 10, 25];
const SHAREABLE_MIN_N = 10;

// Same wide, fixed window as the header selector: the partner directory must
// not gain and lose entries as the report window changes.
const DIRECTORY_WINDOW = "365d" as const;

export function PartnerReportView() {
  const [tenant, setTenant] = useState("");
  const [minN, setMinN] = useState(SHAREABLE_MIN_N);

  const directory = usePoll(
    (s) =>
      api.tenants(
        { window: DIRECTORY_WINDOW, includeTest: includeTestSessions.value },
        s,
      ),
    [includeTestSessions.value],
  );

  const report = usePoll(
    (s) =>
      api.partnerReport(
        {
          tenant,
          window: partnerWindow.value,
          includeTest: includeTestSessions.value,
          minN,
        },
        s,
      ),
    [tenant, partnerWindow.value, includeTestSessions.value, minN],
    { enabled: tenant !== "" },
  );

  const data = report.data;

  return (
    <div style="display: grid; gap: 24px;">
      <section class="card">
        <PanelHelp k="partner.intro" />
        <div class="toolbar" style="flex-wrap: wrap; gap: var(--space-3);">
          <label class="toggle">
            <span>{t("partner.partner")}</span>
            <select
              value={tenant}
              onInput={(e) => setTenant((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t("partner.select")}</option>
              {(directory.data?.tenants ?? []).map((info) => (
                <option key={info.tenant} value={info.tenant}>
                  {tenantLabel(info.tenant)} (
                  {t("partner.sessionsSuffix", { n: formatInt(info.sessions) })})
                </option>
              ))}
            </select>
          </label>
          <label class="toggle">
            <span>
              {t("talentgeo.minN")} <InfoDot k="partner.minN" />
            </span>
            <select
              value={minN}
              onInput={(e) => setMinN(Number((e.target as HTMLSelectElement).value))}
            >
              {MIN_N_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {tenant !== "" && (
            <Staleness
              lastFetched={report.lastFetched}
              loading={report.loading}
              error={report.error}
              onRefresh={report.refetch}
            />
          )}
        </div>
        {directory.data && !directory.data.mapping_configured && (
          <div class="kpi-sub">{t("partner.noMapping")}</div>
        )}
        {minN < SHAREABLE_MIN_N && (
          <div class="kpi-sub hl-orange">
            {t("partner.minNWarn", { n: minN, k: SHAREABLE_MIN_N })}
          </div>
        )}
      </section>

      {tenant === "" && (
        <section class="card">
          <p class="muted">{t("partner.pickAbove")}</p>
        </section>
      )}

      {data && <Report data={data} />}
    </div>
  );
}

function Report({ data }: { data: PartnerReportResponse }) {
  const modes = Object.entries(data.per_mode).sort(
    (a, b) => b[1].total_usd - a[1].total_usd,
  );

  return (
    <>
      <section class="card">
        <h2>{t("partner.reach")}</h2>
        <PanelHelp k="partner.reach" />
        <div class="grid grid-3">
          <Kpi
            label={t("common.sessions")}
            value={formatInt(data.sessions)}
            sub={t("partner.engagedSub", { n: formatInt(data.engaged_sessions) })}
          />
          <Kpi
            label={t("common.turns")}
            value={formatInt(data.turns)}
            sub={t("partner.avgTurns", { n: formatDecimal(data.avg_turns_per_session) })}
          />
          <Kpi
            label={t("partner.satisfaction")}
            value={
              data.feedback_ratio === null ? "—" : formatPercent(data.feedback_ratio)
            }
            sub={
              <FeedbackCounts
                positive={data.feedback_positive}
                negative={data.feedback_negative}
              />
            }
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("partner.cost")}</h2>
        <PanelHelp k="partner.cost" />
        <div class="grid grid-3">
          <Kpi label={t("partner.totalInWindow")} value={formatUsd(data.total_usd)} />
          <Kpi label={t("partner.perTurn")} value={formatUsd(data.usd_per_turn)} />
          <Kpi label={t("partner.perSession")} value={formatUsd(data.usd_per_session)} />
        </div>
        <table class="table" style="margin-top: var(--space-3);">
          <thead>
            <tr>
              <th>{t("common.mode")}</th>
              <th>{t("common.model")}</th>
              <th>{t("common.turns")}</th>
              <th>{t("cost.col.inputTok")}</th>
              <th>{t("cost.col.outputTok")}</th>
              <th>{t("common.cost")}</th>
            </tr>
          </thead>
          <tbody>
            {modes.map(([mode, s]) => (
              <tr key={mode}>
                <td>{modeLabel(mode)}</td>
                <td>{s.model_label || s.model || "—"}</td>
                <td>{formatInt(s.turns)}</td>
                <td>{formatInt(s.input_tokens)}</td>
                <td>{formatInt(s.output_tokens)}</td>
                <td>{formatUsd(s.total_usd)}</td>
              </tr>
            ))}
            {modes.length === 0 && (
              <tr>
                <td colspan={6} class="muted">
                  {t("partner.noTurns")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("partner.quality")}</h2>
        <PanelHelp k="partner.quality" />
        <div class="grid grid-3">
          <Kpi
            label={t("partner.noGoodAnswer")}
            value={formatPercent(data.no_answer_rate)}
            sub={t("partner.turnsN", { n: formatInt(data.no_answer_turns) })}
          />
          <Kpi
            label={t("partner.abortedStreams")}
            value={formatInt(data.streaming_failures)}
            sub={t("partner.abortedSub")}
          />
          <Kpi
            label={t("partner.ratedAnswers")}
            value={formatInt(data.feedback_positive + data.feedback_negative)}
            sub={t("partner.ratedSub")}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("partner.content")}</h2>
        <PanelHelp k="partner.content" />
        <div class="grid grid-3">
          <CountTable title={t("common.mode")} counts={data.mode_distribution} label={modeLabel} />
          <CountTable title={t("partner.language")} counts={data.locale_distribution} />
          <CountTable title={t("partner.intent")} counts={data.top_intents} />
        </div>
        <div class="grid grid-3" style="margin-top: var(--space-3);">
          <CountTable title={t("partner.berufsfeld")} counts={data.top_berufsfelder} />
        </div>
        {data.suppressed_cells > 0 && (
          <div class="kpi-sub">
            {t("partner.suppressed", { n: formatInt(data.suppressed_cells), k: data.k_threshold })}
          </div>
        )}
      </section>

      <section class="card">
        <h2>{t("partner.limits")}</h2>
        <PanelHelp k="partner.limits" />
        <table class="table">
          <tbody>
            <tr>
              <td>{t("partner.mapping")}</td>
              <td>
                {data.configured
                  ? data.origin_patterns.join(", ")
                  : t("partner.builtinBucket")}
              </td>
            </tr>
            <tr>
              <td>{t("partner.hostsSeen")}</td>
              <td>
                {data.origins_seen.length === 0
                  ? "—"
                  : data.origins_seen
                      .map((o) => `${o.origin || "—"} (${formatInt(o.sessions)})`)
                      .join(", ")}
              </td>
            </tr>
            <tr>
              <td>{t("partner.unattributedRow")}</td>
              <td>{t("partner.unattributedText", { n: formatInt(data.unattributed_turns) })}</td>
            </tr>
            <tr>
              <td>{t("partner.notIncluded")}</td>
              <td>{data.utility_calls_excluded ? t("partner.notIncludedText") : "—"}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </>
  );
}

function CountTable({
  title,
  counts,
  label = (k: string) => k,
}: {
  title: string;
  counts: Record<string, number>;
  label?: (key: string) => string;
}) {
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <h3>{title}</h3>
      <table class="table">
        <tbody>
          {rows.map(([key, n]) => (
            <tr key={key}>
              <td>{label(key)}</td>
              <td>{formatInt(n)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td class="muted">—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: ComponentChildren;
}) {
  return (
    <div class="card" style="border-left: 4px solid var(--brand-blue);">
      <h3>{label}</h3>
      <div class="kpi">{value}</div>
      {sub && <div class="kpi-sub">{sub}</div>}
    </div>
  );
}
